import { useEffect, useRef } from 'react';

export interface Hotkey {
  /** Tek karakter ('j', 'k', '/') veya 'Enter', 'Escape', 'ArrowDown'. */
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  /** Form alanında yazarken de çalışsın mı? Ctrl kombinasyonları için otomatik true. */
  allowInInput?: boolean;
  handler: (event: KeyboardEvent) => void;
}

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

/**
 * Klavye kısayolları. PLAN §12.4: J/K, Enter, Esc, Ctrl+Enter, R.
 * Form alanlarında tek harfli kısayollar bastırılır; Ctrl'lüler çalışır.
 */
export function useHotkeys(hotkeys: Hotkey[], enabled = true): void {
  const ref = useRef(hotkeys);
  ref.current = hotkeys;

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.altKey) return;
      const typing = isTypingTarget(event.target);

      for (const hotkey of ref.current) {
        const wantsCtrl = Boolean(hotkey.ctrl);
        if (wantsCtrl !== (event.ctrlKey || event.metaKey)) continue;
        if (Boolean(hotkey.shift) !== event.shiftKey) continue;
        if (event.key.toLowerCase() !== hotkey.key.toLowerCase()) continue;
        if (typing && !wantsCtrl && !hotkey.allowInInput) continue;

        event.preventDefault();
        hotkey.handler(event);
        return;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
